import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, KeyRound, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = 'gst_unlock_token';

export const getGstToken = () => sessionStorage.getItem(KEY) || '';
export const clearGstToken = () => sessionStorage.removeItem(KEY);

// Second lock on top of the login: a private code only the owner knows. Unlock lives in this tab only, 20 minutes.
export default function GSTAccessGate({ children }) {
  const { getAuthHeader } = useAuth();
  const [state, setState] = useState(null); // {has_code, unlocked, is_owner_login}
  const [code, setCode] = useState('');
  const [newCode, setNewCode] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [accountPassword, setAccountPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(false);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API_URL}/gst/access`, { headers: { ...getAuthHeader(), 'X-GST-Token': getGstToken() } });
      setState(r.data);
      if (!r.data.unlocked) clearGstToken();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not check GST access');
    }
  };
  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const unlock = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/gst/access/unlock`, { code }, { headers: getAuthHeader() });
      sessionStorage.setItem(KEY, r.data.unlock_token);
      setCode('');
      setState(s => ({ ...s, unlocked: true }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Incorrect code');
    } finally {
      setBusy(false);
    }
  };

  const setup = async (e) => {
    e.preventDefault();
    if (newCode !== confirmCode) { toast.error('The two codes do not match'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/gst/access/setup`, { account_password: accountPassword, new_code: newCode }, { headers: getAuthHeader() });
      sessionStorage.setItem(KEY, r.data.unlock_token);
      toast.success(changing ? 'GST access code changed' : 'GST access code created');
      setNewCode(''); setConfirmCode(''); setAccountPassword(''); setCode(''); setChanging(false);
      setState(s => ({ ...s, has_code: true, unlocked: true }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the code');
    } finally {
      setBusy(false);
    }
  };

  const lock = () => { clearGstToken(); setState(s => ({ ...s, unlocked: false })); };

  if (!state) return <div className="p-8 text-slate-500 text-sm">Checking access…</div>;

  if (state.unlocked) {
    return (
      <div>
        <div className="flex justify-end gap-3 px-6 pt-4 print:hidden">
          <button type="button" className="text-xs text-slate-500 underline" onClick={() => { setChanging(true); lock(); }} data-testid="gst-change-code">Change code</button>
          <button type="button" className="text-xs text-slate-500 underline inline-flex items-center gap-1" onClick={lock} data-testid="gst-lock-now"><Lock className="w-3 h-3" /> Lock now</button>
        </div>
        {children}
      </div>
    );
  }

  const needsSetup = !state.has_code || changing;
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="gst-lock-screen">
      <form onSubmit={needsSetup ? setup : unlock} className="w-full max-w-sm bg-white border rounded-xl p-6 space-y-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center">{needsSetup ? <ShieldCheck className="w-5 h-5" /> : <Lock className="w-5 h-5" />}</div>
          <div>
            <h2 className="font-heading font-bold uppercase">{needsSetup ? (changing ? 'Change GST access code' : 'Create your GST access code') : 'GST Return is locked'}</h2>
            <p className="text-xs text-slate-500">{needsSetup ? 'A private code only you know. Needed every time this page is opened.' : 'Enter your private access code to continue.'}</p>
          </div>
        </div>

        {needsSetup ? (
          state.is_owner_login ? (
            <>
              <Input type="password" placeholder="New code (4–32 characters)" value={newCode} onChange={e => setNewCode(e.target.value)} minLength={4} maxLength={32} required data-testid="gst-new-code" />
              <Input type="password" placeholder="Repeat new code" value={confirmCode} onChange={e => setConfirmCode(e.target.value)} required data-testid="gst-confirm-code" />
              <Input type="password" placeholder="Your account password (to confirm it's you)" value={accountPassword} onChange={e => setAccountPassword(e.target.value)} required data-testid="gst-account-password" />
              <Button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="gst-setup-submit"><KeyRound className="w-4 h-4 mr-2" /> {changing ? 'Change code' : 'Create code & open'}</Button>
              {changing && <button type="button" className="text-xs text-slate-500 underline w-full" onClick={() => setChanging(false)}>Cancel</button>}
            </>
          ) : (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="gst-owner-required">The owner has not set up a GST access code yet. Only the owner's own login can create it.</p>
          )
        ) : (
          <>
            <Input type="password" inputMode="numeric" placeholder="Access code" value={code} onChange={e => setCode(e.target.value)} autoFocus required data-testid="gst-unlock-code" />
            <Button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="gst-unlock-submit"><KeyRound className="w-4 h-4 mr-2" /> Unlock</Button>
            {state.is_owner_login && <button type="button" className="text-xs text-slate-500 underline w-full" onClick={() => setChanging(true)} data-testid="gst-forgot-code">Forgot the code? Reset it with your account password</button>}
          </>
        )}
      </form>
    </div>
  );
}
